import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';

import { environment } from '@env/environment';
import { AuthenticationService } from '@app/auth';
import { I18nService } from '@app/i18n';
import { ButtonPadding, ButtonVariant, PlButtonType } from '@app/components/button/button.component';
import { HttpErrorResponse } from '@angular/common/http';
import { PlInputComponent, PlInputType } from '@app/components/pl-input/pl-input.component';
import { plValidateEmail } from '@app/components/pl-input/email-validator';
import { finalize } from 'rxjs/operators';
import { getRoutePath, PlRoute } from '@app/utils/routes';
import { PlRouter } from '@shared/services/pl-router.service';

enum FormControlName {
  email = 'email',
}

interface ForgotPasswordForm {
  [FormControlName.email]: FormControl<string | null>;
}

export interface QueryParamsForgotPassword {
  errorMessage?: string;
}

@Component({
  selector: 'pl-forgot-password',
  templateUrl: './forgot-password.component.html',
})
export class ForgotPasswordComponent implements OnInit {
  public version: string | null = environment.version;
  public error: string | undefined;
  public forgotPasswordForm!: FormGroup<ForgotPasswordForm>;
  public isLoading: boolean = false;

  protected readonly ButtonPadding: typeof ButtonPadding = ButtonPadding;
  protected readonly ButtonVariant: typeof ButtonVariant = ButtonVariant;
  protected readonly FormControlName: typeof FormControlName = FormControlName;
  protected readonly PlButtonType: typeof PlButtonType = PlButtonType;
  protected readonly PlInputType: typeof PlInputType = PlInputType;

  public showEmailSentSection: boolean = false;

  @ViewChild('plInputForgotPassword') public plInputForgotPassword!: PlInputComponent;

  constructor(
    private authenticationService: AuthenticationService,
    private i18nService: I18nService,
    private route: ActivatedRoute,
    private plRouter: PlRouter,
  ) {
    this.createForm();
  }

  public ngOnInit(): void {
    const queryParams: QueryParamsForgotPassword | undefined = this.route.snapshot
      .queryParams as QueryParamsForgotPassword;
    if (queryParams && queryParams.errorMessage) {
      this.error = queryParams.errorMessage;
    }
  }

  public resetPassword(): void {
    this.updateForgotPasswordInputFromAutocomplete();
    if (!this.forgotPasswordForm.valid) {
      this.forgotPasswordForm.controls.email.markAsDirty();
    } else {
      this.isLoading = true;
      const email: string = this.forgotPasswordForm.controls.email.value as string;
      this.authenticationService
        .forgotPassword(email)
        .pipe(
          finalize((): void => {
            this.forgotPasswordForm.markAsPristine();
            this.isLoading = false;
          }),
        )
        .subscribe({
          next: (): void => {
            this.showEmailSentSection = true;
          },
          error: (error: HttpErrorResponse): void => {
            console.error('There was an error sending reset password email, error=', error);
            this.error = this.i18nService.translate(`login.errors.unexpected_error`);
          },
        });
    }
  }

  private createForm(): void {
    const fb: FormBuilder = new FormBuilder();
    this.forgotPasswordForm = fb.nonNullable.group<ForgotPasswordForm>({
      email: fb.control<string>('', [Validators.required, plValidateEmail]),
    });
  }

  private updateForgotPasswordInputFromAutocomplete(): void {
    const angularForgotPasswordValue: string | null = this.forgotPasswordForm.controls.email.value;
    if (!angularForgotPasswordValue) {
      const innerHTMLForgotPasswordValue: string = this.plInputForgotPassword.inputElement.nativeElement.value;

      if (innerHTMLForgotPasswordValue) {
        this.forgotPasswordForm.controls.email.setValue(innerHTMLForgotPasswordValue);
        this.forgotPasswordForm.controls.email.markAsDirty();
      }
    }
  }

  public showLoginScreen(): void {
    this.plRouter.navigate([getRoutePath(PlRoute.login)]);
  }
}
